/**
 * What the person sees after a code does not work — and what analytics records.
 *
 * MSG91 already distinguishes "wrong code" from "expired code"
 * (`src/lib/otp/msg91.ts`), but that distinction used to die inside
 * `verifyPhoneOtp`: both became a bare `false`, Better Auth raised a generic
 * `INVALID_OTP`, and the UI showed its raw text. So "your code expired, get a
 * new one" and "you mistyped, try again" rendered identically, and the
 * `phone_verification_failed` event carried no reason — which is why a funnel
 * of "requested twice, then completed" could not be split into
 * SMS-never-arrived vs typo vs expired-code.
 *
 * The contract:
 *
 * - `verifyPhoneOtp` throws `APIError(BAD_REQUEST, { code:
 *   OTP_VERIFY_EXPIRED_CODE })` for an expired code and returns `false` for a
 *   wrong one. The code survives the round trip: Better Auth serialises the
 *   error body, so the client reads it back off `result.error`.
 * - Both UI paths (sign-in verify, attach verify) map the error through
 *   `describeOtpVerifyError` and render the copy here — never the raw
 *   provider text.
 * - `failed` analytics carry `failure_code` from the same parse, so PostHog
 *   can group by it.
 *
 * The expired/wrong split is deliberately the ONLY distinction exposed. Per
 * AUTH-06, "no account has this number" vs "wrong code" vs "belongs to someone
 * else" stays one opaque message — that difference is what an attacker wants
 * to learn, and it is of no use to a real person. Expired vs wrong only
 * changes which button the person should press next.
 *
 * Client-safe: no server imports. The form and the server action share this.
 */

/** Thrown by `verifyPhoneOtp` for an expired code; read back off the client error. */
export const OTP_VERIFY_EXPIRED_CODE = "OTP_EXPIRED";

/** A wrong code was submitted. The current code is still alive — invite a retry. */
export const OTP_VERIFY_MISMATCH_MESSAGE =
  "That code doesn't match. Check the latest message and try again.";

/**
 * The code is dead. Retrying it can never succeed, so the UI must stop
 * inviting retries (`needsNewCode`) and point at the SMS resend button, which
 * issues a genuinely new code in this case.
 */
export const OTP_VERIFY_EXPIRED_MESSAGE =
  "That code has expired — codes last 5 minutes. Tap SMS below for a new one.";

export type OtpVerifyFailureKind =
  /** Dead code: resend, don't retry. */
  | "expired"
  /** Live code, wrong value: retry. */
  | "mismatch"
  /** Our own 5-guesses-per-15-minutes budget tripped: server message stands. */
  | "rate_limited"
  /** Provider wobble: server message stands. */
  | "unavailable"
  /** Anything unrecognised: rendered as a mismatch, never a raw dump. */
  | "unknown";

export interface OtpVerifyFeedback {
  kind: OtpVerifyFailureKind;
  /** Copy to render. Server-authored for rate limits/outages, ours otherwise. */
  message: string;
  /** True when retrying the current value is futile — force the resend path. */
  needsNewCode: boolean;
  /**
   * Normalised raw code (`OTP_EXPIRED`, `INVALID_OTP`, `TOO_MANY_REQUESTS`,
   * …) for the `failure_code` analytics property. Null when the error carried
   * nothing parseable.
   */
  failureCode: string | null;
}

function readErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const record = error as Record<string, unknown>;
  const nested =
    record.error && typeof record.error === "object"
      ? (record.error as Record<string, unknown>)
      : null;
  const nestedBody =
    nested?.body && typeof nested.body === "object"
      ? (nested.body as Record<string, unknown>)
      : null;
  const body =
    record.body && typeof record.body === "object"
      ? (record.body as Record<string, unknown>)
      : null;
  for (const candidate of [
    record.code,
    body?.code,
    nested?.code,
    nestedBody?.code,
  ]) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim().toUpperCase();
    }
  }
  return null;
}

function readErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const record = error as Record<string, unknown>;
  for (const candidate of [record.status, record.statusCode]) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return candidate;
    }
  }
  return null;
}

function serverMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const message = (error as Record<string, unknown>).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return fallback;
}

/**
 * Maps a verify-round-trip error to renderable feedback. Accepts the
 * better-auth client shape (`{ code, message, status }`), the server-action
 * `{ code, message }` convention, and nesting of either under `error`/`body`.
 */
export function describeOtpVerifyError(error: unknown): OtpVerifyFeedback {
  const code = readErrorCode(error);
  const status = readErrorStatus(error);

  if (code === OTP_VERIFY_EXPIRED_CODE || code === "OTP_NOT_FOUND") {
    return {
      kind: "expired",
      message: OTP_VERIFY_EXPIRED_MESSAGE,
      needsNewCode: true,
      failureCode: code,
    };
  }
  if (code === "TOO_MANY_REQUESTS" || status === 429) {
    return {
      kind: "rate_limited",
      message: serverMessage(
        error,
        "Too many attempts. Try again in a few minutes, or continue with Google or Apple.",
      ),
      needsNewCode: false,
      failureCode: code ?? "TOO_MANY_REQUESTS",
    };
  }
  if (
    code === "SERVICE_UNAVAILABLE" ||
    code === "SEND_OTP_NOT_IMPLEMENTED" ||
    status === 503 ||
    status === 500
  ) {
    return {
      kind: "unavailable",
      message: serverMessage(
        error,
        "We couldn't check that code. Please try again.",
      ),
      needsNewCode: false,
      failureCode: code ?? "SERVICE_UNAVAILABLE",
    };
  }
  if (
    code === "INVALID_OTP" ||
    code === "OTP_INVALID" ||
    code === "PHONE_NUMBER_NOT_VERIFIED"
  ) {
    return {
      kind: "mismatch",
      message: OTP_VERIFY_MISMATCH_MESSAGE,
      needsNewCode: false,
      failureCode: code,
    };
  }
  return {
    kind: "unknown",
    message:
      typeof error === "object" && error !== null
        ? serverMessage(error, OTP_VERIFY_MISMATCH_MESSAGE)
        : OTP_VERIFY_MISMATCH_MESSAGE,
    needsNewCode: false,
    failureCode: code,
  };
}
